// Everything the process map needs that is *not* the viewport: instance scope, the render graph,
// its ELK layout, and the current selection. One call owns one map. Pan/zoom, the app highlight
// and the heat ramp stay in ProcessGraphCanvas — they belong to a viewport, not to a process.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getBrowserHash, updateBrowserHash } from "../../utils/urlHash";
import {
  PROCESS_COMPARISON_HASH_KEY,
  type PageOverride,
  pageOverride,
  processComparisonHash,
  processScopeHashParams,
  readProcessCase,
  readProcessComparePair,
  readProcessStep,
  readProcessUrlScope,
  serializeComparison,
  writeProcessCase,
  writeProcessComparePair,
  writeProcessStep,
} from "./processViewUrl";

import { type ChatScopeFilters, filter_process_instances } from "../../api";
import type { Process } from "../../api/types";
import { selectCommonPaths } from "./processCommonPaths";
import {
  type ComparePair,
  type CompareSide,
  sameComparePair,
  withCompareSide,
} from "./processCompareModel";
import type { ProcessComparison } from "./processComparison";
import { comparisonGraphData } from "./processComparisonGraph";
import type { EdgeRoutingMode } from "./processGraphLayout";
import { useElkLayout } from "./useElkLayout";
import {
  type ColorMode,
  type MapFocus,
  SIMPLE_MAP_LIMIT,
  type ProcessEdge,
  type ProcessGraphNode,
  type ProcessInstance,
  type Selection,
  type StepReading,
  graphOnRoutes,
  graphWithInstances,
  stepTimeStats,
} from "./processGraphModel";
import {
  processRecordingInstanceNumbersBySourceKey,
  processSourceInstanceKey,
} from "./processInstanceIdentity";
import {
  processSourceInstanceIdentity,
  shouldShowProcessSourceUserName,
} from "./processInstanceLabels";
import {
  EMPTY_INSTANCE_SCOPE,
  activeScopeCriteria,
  type InstanceScope,
  type InstanceScopeIndex,
  type InstanceScopeQuery,
  type ScopeQueryStatus,
  buildInstanceScopeIndex,
  instanceScopeFromIds,
  isDefaultInstanceScope,
  matchInstanceScope,
  sameInstanceScope,
} from "./processScopeModel";

/** The view compare was entered from, kept so leaving can put it back. */
type CompareOrigin = {
  scope: InstanceScope;
  /** The focused instance, when compare was entered from a single-instance view. */
  instanceId: string | null;
  /** The step the timeline was resting on, when it was one of that instance's own. */
  stepIndex: number | null;
};

/** With an inherited scope, people and dates live in the page override rather than here. */
const criteriaOnly = (scope: InstanceScope): InstanceScope => ({
  ...scope,
  userIds: [],
  noUsers: false,
  from: null,
  to: null,
});

const initialInstanceScope = (
  index: InstanceScopeIndex,
  defaultInstanceId?: string,
): InstanceScope =>
  defaultInstanceId && index.order.includes(defaultInstanceId)
    ? instanceScopeFromIds(index, [defaultInstanceId])
    : EMPTY_INSTANCE_SCOPE;

/** The compared pair from the hash, but only while both ids still name instances of this process. */
const comparePairFromHash = (index: InstanceScopeIndex): ComparePair | null => {
  const fromHash = readProcessComparePair();
  if (!fromHash) return null;
  const known = new Set(index.order);
  return known.has(fromHash[0]) && known.has(fromHash[1])
    ? { aId: fromHash[0], bId: fromHash[1] }
    : null;
};

/**
 * Scope survives leaving the page, and a reload with it. Navigating to a step's recording and
 * back is one look at one process. Storage is a fallback for visits whose URL names no scope (an
 * unfiltered scope writes no params); a shared URL always wins, including its page overrides.
 *
 * sessionStorage rather than localStorage: a scope is about one sitting with one process, so it
 * should not still be there next week.
 */
// v3: earlier entries always spelled out Simplified or All paths, so restoring one would pin
// every process visited before the size-based default existed to the choice made for it.
const SCOPE_HISTORY_STORAGE_PREFIX = "clank.process-scope.v3:";

/** Everything remembered for one process, whichever caller defaults produced it. */
const scopeStorageProcessPrefix = (slug: string) => `${SCOPE_HISTORY_STORAGE_PREFIX + slug}\u0000`;

/** One process, one set of caller defaults — the inputs that decide the scope. */
const scopeStorageKey = (slug: string, defaultInstanceId?: string) =>
  scopeStorageProcessPrefix(slug) + (defaultInstanceId ?? "");

/**
 * What the process looked like when the scope was saved. A re-run analysis rebuilds the process
 * from scratch — different instances, different ids — and a scope saved against the old set is
 * meaningless against the new one, however well its ids happen to line up. Comparing this is how
 * a re-run is noticed at all, since nothing else about the page changes.
 */
const processInstanceFingerprint = (index: InstanceScopeIndex): string =>
  `${index.order.length}:${[...index.order].sort().join(",")}`;

type StoredScope = { applied: InstanceScope; fingerprint: string };

/** Drops every scope remembered for this process — used when its instances have been rebuilt. */
const forgetProcessScopes = (slug: string) => {
  try {
    const prefix = scopeStorageProcessPrefix(slug);
    const stale = Object.keys(window.sessionStorage).filter(key => key.startsWith(prefix));
    for (const key of stale) window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up if storage is not readable in the first place.
  }
};

/**
 * The remembered scope for this process, or nothing — because none was saved, because the saved
 * one is unreadable, or because the analysis has been re-run since. The last case wipes the
 * process's other saved scopes too: they were all written against instances that no longer exist.
 */
const readStoredScope = (
  key: string,
  slug: string,
  index: InstanceScopeIndex,
): InstanceScope | undefined => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<StoredScope>;
    const { applied, fingerprint } = parsed ?? {};
    if (!applied || typeof fingerprint !== "string") return undefined;
    if (fingerprint !== processInstanceFingerprint(index)) {
      forgetProcessScopes(slug);
      return undefined;
    }
    return applied;
  } catch {
    return undefined;
  }
};

const writeStoredScope = (key: string, applied: InstanceScope, index: InstanceScopeIndex) => {
  try {
    const stored: StoredScope = {
      applied,
      fingerprint: processInstanceFingerprint(index),
    };
    window.sessionStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // Storage being unavailable costs the user a remembered scope, never the page.
  }
};

/**
 * Second line of defence behind the fingerprint: a scope is only restored while it still picks
 * out at least one instance, or explicitly requests Common paths (which may have no support).
 */
const restoreScope = (
  stored: InstanceScope | undefined,
  index: InstanceScopeIndex,
): InstanceScope | null => {
  if (!stored) return null;
  const alive =
    isDefaultInstanceScope(stored) ||
    stored.commonPaths ||
    activeScopeCriteria(stored).length > 0 ||
    matchInstanceScope(index, stored).includedIds.length > 0;
  return alive ? stored : null;
};

// Stable fallbacks while the first (async) layout is still computing, so hook
// dependencies don't churn render over render.
const EMPTY_NODES: ProcessGraphNode[] = [];
const EMPTY_EDGES: ProcessEdge[] = [];
const EMPTY_REVERSED_MAP = new Map<string, boolean>();

type UseProcessGraphOptions = {
  process: Process;
  inheritedScope?: Pick<InstanceScope, "userIds" | "noUsers" | "from" | "to">;
  /** People in the current page scope; forwarded to chat. */
  userIdList?: string;
  // Instance shown initially; an unknown/absent id falls back to the all-instances view.
  defaultInstanceId?: string;
  /** An explicit action from another page overrides remembered and dashboard scope on entry. */
  initialScope?: InstanceScope;
  initialComparison?: ProcessComparison;
  /** The page owns history entries; changes within an existing compare view stay local. */
  onEnterCompare?: (hash: string) => void;
  /** Returns true when exiting navigates back to the view that opened compare. */
  onExitCompare?: () => boolean;
};

export type ProcessGraphState = ReturnType<typeof useProcessGraph>;

export const useProcessGraph = ({
  process,
  inheritedScope,
  userIdList,
  defaultInstanceId,
  initialScope,
  initialComparison,
  onEnterCompare,
  onExitCompare,
}: UseProcessGraphOptions) => {
  const { t } = useTranslation();
  const [urlScope] = useState(() => readProcessUrlScope());
  const inheritedScopeKey = JSON.stringify(inheritedScope);
  const hasInheritedScope = !!inheritedScope;
  const [comparison, setComparison] = useState<ProcessComparison | null>(initialComparison ?? null);
  const comparisonData = useMemo(
    () => (comparison ? comparisonGraphData(comparison) : null),
    [comparison],
  );
  const [initialCase] = useState(() => {
    if (initialComparison || readProcessComparePair()) return null;
    const pick = readProcessCase();
    const instance = process.instances.find(run => run.id === pick?.instanceId);
    const step = pick && instance?.steps[pick.stepIndex];
    return step && pick ? { ...pick, nodeId: step.nodeId } : null;
  });
  // Read once at mount, so a later hash write cannot retroactively change what the map opened on.
  const [selection, setSelection] = useState<Selection | null>(() => {
    if (initialCase)
      return {
        kind: "node",
        id: initialCase.nodeId,
        occurrence: { instanceId: initialCase.instanceId, stepIndex: initialCase.stepIndex },
      };
    const id = readProcessStep();
    return id ? { kind: "node", id } : null;
  });
  // Inspecting a case temporarily draws its complete route without editing the saved scope.
  const [highlightedInstanceId, setHighlightedInstanceId] = useState<string | null>(
    initialCase?.instanceId ?? null,
  );
  // Pointing at a step's branch row in the side panel previews it on the map exactly as hovering
  // the route itself does: the transition and the step it leads to, nothing else. Like the
  // focused instance it is an overlay on the pooled graph, not a selection.
  const [branchHoverEdge, setBranchHoverEdge] = useState<{ from: string; to: string } | null>(null);
  // The part of the map a panel tab is reading — a path's route, an app's steps, the row under
  // the pointer — lit over the pooled graph. It yields to an open case or selection (see the canvas).
  const [mapFocus, setMapFocus] = useState<MapFocus | null>(null);
  // Defaults: duration tint, automatic step readings, separate connections and traffic off.
  const [colorBy, setColorBy] = useState<ColorMode>("time");
  const [stepReading, setStepReading] = useState<StepReading>("auto");
  const [edgeRouting, setEdgeRouting] = useState<EdgeRoutingMode>("separate");
  const [traffic, setTraffic] = useState(false);
  const [simpleMapStepLimit, setSimpleMapStepLimit] = useState(SIMPLE_MAP_LIMIT.steps);
  const simpleMapLimit = useMemo(
    () => ({ steps: simpleMapStepLimit, connections: simpleMapStepLimit * 5 }),
    [simpleMapStepLimit],
  );

  const [runComparePair, setComparePair] = useState<ComparePair | null>(() =>
    comparePairFromHash(buildInstanceScopeIndex(process.instances ?? [], process.nodes)),
  );
  const processInstances = process.instances ?? [];
  const scopeIndex = useMemo(
    () => buildInstanceScopeIndex(processInstances, process.nodes),
    [processInstances, process.nodes],
  );
  const scopeStoreKey =
    scopeStorageKey(process.slug, defaultInstanceId) + (inheritedScope ? "\0shared" : "");
  const [localScope, setAppliedScope] = useState<InstanceScope>(() => {
    const index = buildInstanceScopeIndex(process.instances ?? [], process.nodes);
    if (urlScope)
      return hasInheritedScope ? urlScope.scope : { ...urlScope.scope, ...urlScope.override };
    if (initialScope) return hasInheritedScope ? criteriaOnly(initialScope) : initialScope;
    // Compare draws its own cases. With no base criteria in its URL, return to the default
    // scope on exit rather than borrowing another visit's remembered filters.
    if (comparePairFromHash(index) || initialComparison) return EMPTY_INSTANCE_SCOPE;
    const restored = restoreScope(readStoredScope(scopeStoreKey, process.slug, index), index);
    if (restored) return restored;
    return initialInstanceScope(index, defaultInstanceId);
  });
  // Dashboard filters seed each visit. Picker edits override them only in this workspace;
  // a new inherited selection or another process starts from the dashboard again.
  const [pageScope, setPageScope] = useState<{
    inheritedKey: string | undefined;
    scope: PageOverride;
  } | null>(() => {
    const requested = urlScope?.override ?? initialScope;
    const override =
      inheritedScope && requested
        ? pageOverride({ ...EMPTY_INSTANCE_SCOPE, ...inheritedScope, ...requested }, inheritedScope)
        : null;
    return override && { inheritedKey: inheritedScopeKey, scope: override };
  });
  const activeOverride =
    pageScope?.inheritedKey === inheritedScopeKey ? pageScope?.scope : undefined;
  const effectiveScope = inheritedScope && { ...inheritedScope, ...activeOverride };
  const appliedScope = useMemo(
    () => (effectiveScope ? { ...localScope, ...effectiveScope } : localScope),
    [localScope, JSON.stringify(effectiveScope)],
  );
  const processInstanceIds = scopeIndex.order;
  // Picking a transition the map leaves out draws every case in scope while the pick is looked
  // at. It is a view rather than a choice of scope: nothing remembers or shares it, and the map
  // selection ends it once nothing picked on the revealed paths is open.
  const [allPathsRevealed, setAllPathsRevealed] = useState(false);
  const drawnScope = useMemo(
    () =>
      allPathsRevealed ? { ...appliedScope, commonPaths: false, excludedIds: [] } : appliedScope,
    [allPathsRevealed, appliedScope],
  );
  const drawnMatch = useMemo(
    () => matchInstanceScope(scopeIndex, drawnScope, simpleMapLimit),
    [scopeIndex, drawnScope, simpleMapLimit],
  );
  const simplified = !comparisonData && !runComparePair && drawnMatch.simplified;
  const filteredInstances = useMemo(() => {
    if (comparisonData) return comparisonData.process.instances;
    if (runComparePair)
      return processInstances.filter(
        instance => instance.id === runComparePair.aId || instance.id === runComparePair.bId,
      );
    const ids = new Set(
      matchInstanceScope(scopeIndex, { ...appliedScope, commonPaths: false, excludedIds: [] })
        .matchedIds,
    );
    return processInstances.filter(instance => ids.has(instance.id));
  }, [comparisonData, runComparePair, scopeIndex, appliedScope, processInstances]);
  // Simplify is offered only where it would leave cases out; elsewhere the click changes nothing.
  const canSimplify = useMemo(
    () =>
      !comparisonData &&
      !runComparePair &&
      !simplified &&
      selectCommonPaths(filteredInstances, simpleMapLimit).ids.size < filteredInstances.length,
    [comparisonData, runComparePair, simplified, filteredInstances, simpleMapLimit],
  );
  // Empty filters and hand-picked intersections must stay empty.
  const selectedInstanceIds = useMemo(
    () =>
      comparisonData
        ? comparisonData.process.instances.map(run => run.id)
        : runComparePair
          ? scopeIndex.order.filter(id => id === runComparePair.aId || id === runComparePair.bId)
          : drawnMatch.includedIds,
    [comparisonData, runComparePair, scopeIndex, drawnMatch.includedIds],
  );
  const allInstancesSelected =
    processInstanceIds.length > 0 && selectedInstanceIds.length === processInstanceIds.length;
  const selectedInstances = useMemo(() => {
    if (comparisonData) return comparisonData.process.instances;
    const selectedSet = new Set(selectedInstanceIds);
    return processInstances.filter(instance => selectedSet.has(instance.id));
  }, [comparisonData, processInstances, selectedInstanceIds]);

  // Applying a new scope closes the inspector; browsing an unselected case does not edit it.
  const inspectedScopeRef = useRef(appliedScope);
  useEffect(() => {
    if (inspectedScopeRef.current !== appliedScope) setHighlightedInstanceId(null);
    inspectedScopeRef.current = appliedScope;
  }, [appliedScope]);

  useEffect(() => {
    const occurrence = selection?.kind === "node" ? selection.occurrence : undefined;
    writeProcessCase(
      !runComparePair && !comparison && occurrence?.instanceId === highlightedInstanceId
        ? occurrence
        : null,
    );
  }, [highlightedInstanceId, selection, runComparePair, comparison]);
  const highlightedInstance = useMemo(
    () =>
      highlightedInstanceId
        ? ((comparisonData?.process.instances ?? processInstances).find(
            instance => instance.id === highlightedInstanceId,
          ) ?? null)
        : null,
    [highlightedInstanceId, comparisonData, processInstances],
  );
  const inspectedInstanceGraph = useMemo(
    () =>
      highlightedInstance && !comparisonData && !runComparePair
        ? graphWithInstances(process.nodes, [highlightedInstance])
        : null,
    [highlightedInstance, comparisonData, runComparePair, process.nodes],
  );

  const [scopeQueryStatus, setScopeQueryStatus] = useState<ScopeQueryStatus>("idle");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const scopeQueryRequestId = useRef(0);
  const analysisGraph = useMemo(
    () => graphWithInstances(comparisonData?.process.nodes ?? process.nodes, filteredInstances),
    [comparisonData, process.nodes, filteredInstances],
  );
  // A simplified map draws the common paths but counts every case in scope on them. Hand-picked
  // cases, compared runs and an inspected case are what their own numbers describe.
  const graph = useMemo(() => {
    if (inspectedInstanceGraph) return inspectedInstanceGraph;
    if (simplified) return graphOnRoutes(analysisGraph, selectedInstances);
    // Usually every case in scope is selected: that graph is the analysis graph already.
    const sameCases =
      selectedInstances.length === filteredInstances.length &&
      selectedInstances.every((instance, index) => instance === filteredInstances[index]);
    return sameCases
      ? analysisGraph
      : graphWithInstances(comparisonData?.process.nodes ?? process.nodes, selectedInstances);
  }, [
    inspectedInstanceGraph,
    simplified,
    analysisGraph,
    comparisonData,
    process.nodes,
    selectedInstances,
    filteredInstances,
  ]);

  const {
    layout: layoutState,
    running: layoutRunning,
    failed: layoutFailed,
  } = useElkLayout(graph, edgeRouting);
  // Mark new inputs pending during render, before the effect starts the worker.
  // Otherwise the canvas can consume its auto-fit request against the old layout.
  const layoutPending =
    layoutRunning ||
    (!layoutFailed && (layoutState?.sourceGraph !== graph || layoutState?.routing !== edgeRouting));

  const layout = layoutState?.layout;
  const nodes = layoutState?.nodes ?? EMPTY_NODES;
  const edges = layoutState?.edges ?? EMPTY_EDGES;
  const reversedMap = layoutState?.reversedMap ?? EMPTY_REVERSED_MAP;

  const selectedNode =
    selection?.kind === "node"
      ? ((inspectedInstanceGraph ?? analysisGraph).nodes.find(n => n.id === selection.id) ?? null)
      : null;

  // Statistics for the currently drawn graph. Panel summaries use analysisGraph.
  const stepStats = useMemo(() => stepTimeStats(graph, nodes), [graph, nodes]);
  const nameById = useMemo(
    () => new Map((comparisonData?.process.nodes ?? process.nodes).map(n => [n.id, n.name])),
    [comparisonData, process.nodes],
  );

  // The caller's inputs changing (a different process, a different user filter) resets the scope
  // rather than merging into it, and clears the history with it: back would otherwise walk into
  // scopes describing instances that are no longer on screen. Same inputs — including a remount
  // after navigating away — keep what was applied, restoring it from the store if this mount is
  // the one that came back.
  const scopeKeyRef = useRef(scopeStoreKey);
  useEffect(() => {
    if (scopeKeyRef.current === scopeStoreKey) return;
    scopeKeyRef.current = scopeStoreKey;
    setAllPathsRevealed(false);
    const restored = restoreScope(
      readStoredScope(scopeStoreKey, process.slug, scopeIndex),
      scopeIndex,
    );
    if (restored) {
      setAppliedScope(restored);
      return;
    }
    const initial = initialInstanceScope(scopeIndex, defaultInstanceId);
    setAppliedScope(current => (sameInstanceScope(current, initial) ? current : initial));
  }, [scopeStoreKey, scopeIndex, defaultInstanceId]);

  useEffect(() => {
    writeStoredScope(scopeStoreKey, localScope, scopeIndex);
  }, [scopeStoreKey, localScope, scopeIndex]);

  // Criteria and temporary page overrides travel separately from global People/Dates.
  // Write on mount as well: a remembered/default scope must be reproducible in another tab.
  useEffect(() => {
    updateBrowserHash(processScopeHashParams(localScope, activeOverride));
  }, [localScope, activeOverride]);

  useEffect(() => {
    updateBrowserHash({
      [PROCESS_COMPARISON_HASH_KEY]: comparison ? serializeComparison(comparison) : null,
    });
  }, [comparison]);

  // A selection survives the instance switch as long as it is still in the rebuilt graph;
  // one that doesn't is dropped along with its echo in the hash.
  useEffect(() => {
    if (!selection) return;
    const isValid =
      selection.kind === "node"
        ? (inspectedInstanceGraph ?? analysisGraph).nodes.some(n => n.id === selection.id)
        : graph.edges.some(e => e.from === selection.from && e.to === selection.to);
    if (isValid) return;
    setSelection(null);
    if (selection.kind === "node") {
      writeProcessStep(null);
    }
  }, [graph, analysisGraph, inspectedInstanceGraph, selection]);

  // The single entry point for changing what the panel reflects.
  const select = useCallback((sel: Selection | null) => {
    setSelection(sel);
    writeProcessStep(sel?.kind === "node" ? sel.id : null);
  }, []);

  const showInstanceUserName = useMemo(
    () => shouldShowProcessSourceUserName(processInstances.map(instance => instance.source)),
    [processInstances],
  );

  const recordingInstanceNumbers = useMemo(
    () => processRecordingInstanceNumbersBySourceKey(process.sources),
    [process.sources],
  );

  const instanceOptionIdentity = useCallback(
    (instance: ProcessInstance) => {
      const instanceNumber = recordingInstanceNumbers.get(
        processSourceInstanceKey(instance.source),
      );
      return processSourceInstanceIdentity({
        source: instance.source,
        instanceLabel: t("process_instance_n", { num: instanceNumber ?? 1 }),
        fallbackRecordingName: t("recording_session"),
        showUserName: showInstanceUserName,
        includeStartTime: false,
      });
    },
    [recordingInstanceNumbers, showInstanceUserName, t],
  );

  const instanceFilterLabel = useMemo(() => {
    if (allInstancesSelected) {
      return t("all_n_instances", { count: processInstances.length });
    }
    return t("selected_n_of_total_instances", {
      count: selectedInstances.length,
      total: processInstances.length,
    });
  }, [allInstancesSelected, processInstances.length, selectedInstances.length, t]);

  // Snapshot every selected run, including All, so later data cannot change this conversation.
  const chatFilters: ChatScopeFilters = useMemo(() => {
    const chatUserIds = hasInheritedScope
      ? appliedScope.noUsers
        ? []
        : appliedScope.userIds.length > 0
          ? appliedScope.userIds
          : undefined
      : userIdList?.split(",");
    return {
      processes: comparison
        ? [...new Set([comparison.a.process.name, comparison.b.process.name])]
        : [process.name],
      userIds: comparison || runComparePair ? undefined : chatUserIds,
      instanceIds: comparison
        ? [...new Set([...comparison.a.runs, ...comparison.b.runs].map(run => run.id))]
        : (runComparePair ? selectedInstances : filteredInstances).map(instance => instance.id),
    };
  }, [
    hasInheritedScope,
    appliedScope.noUsers,
    appliedScope.userIds,
    userIdList,
    comparison,
    process.name,
    runComparePair,
    selectedInstances,
    filteredInstances,
  ]);
  // Keyed by process alone, so changing the user filter continues the same transcript.
  const chatSessionKey = `process:${process.slug}`;
  const chatSuggestions = useMemo(
    () => [
      t("chat_suggestion_time_sink"),
      t("chat_suggestion_differences"),
      t("chat_suggestion_summary"),
    ],
    [t],
  );

  // Filters apply immediately, including empty results and empty intersections with chosen cases.
  const applyScope = useCallback(
    (next: InstanceScope) => {
      // Keep per-page people/date overrides out of the saved dashboard selection.
      if (inheritedScope) {
        const override = pageOverride(next, inheritedScope);
        setPageScope(override && { inheritedKey: inheritedScopeKey, scope: override });
      }
      const local = inheritedScope ? criteriaOnly(next) : next;
      // Any choice ends a reveal, including re-applying the scope it was drawn over.
      setAllPathsRevealed(false);
      setAppliedScope(current => {
        if (sameInstanceScope(current, local)) return current;
        return local;
      });
    },
    [inheritedScope, inheritedScopeKey],
  );

  // ---------------------------------------------------------------------------
  // Compare
  //
  // A pair draws exactly its two routes over the base scope, like a group comparison. Keeping
  // the base scope intact makes the link compact and gives direct loads a useful view on exit.
  const comparePair = comparisonData?.pair ?? runComparePair;
  // Where comparing was entered from. Compare is almost always started from one instance's
  // timeline, and leaving it should put that view back exactly as it was rather than dropping
  // the user on a two-instance map they never asked to look at on its own.
  const [compareOrigin, setCompareOrigin] = useState<CompareOrigin | null>(() =>
    initialComparison && initialScope
      ? { scope: initialScope, instanceId: null, stepIndex: null }
      : null,
  );

  /** Enter compare (or re-aim it): the pair becomes the scope, and the pair itself is recorded. */
  const startCompare = useCallback(
    (pair: ComparePair) => {
      if (pair.aId === pair.bId) return;
      const known = new Set(scopeIndex.order);
      if (!known.has(pair.aId) || !known.has(pair.bId)) return;
      if (!comparePair && onEnterCompare) {
        onEnterCompare(processComparisonHash(getBrowserHash(), [pair.aId, pair.bId]));
        return;
      }
      // Only entering records an origin: re-aiming a side is still the same visit to compare,
      // and must not overwrite the view the visit started from.
      if (!comparePair) {
        setCompareOrigin({
          scope: appliedScope,
          instanceId: highlightedInstanceId ?? null,
          stepIndex:
            highlightedInstanceId &&
            selection?.kind === "node" &&
            selection.occurrence?.instanceId === highlightedInstanceId
              ? selection.occurrence.stepIndex
              : null,
        });
      }
      setComparison(null);
      setComparePair(current => (sameComparePair(current, pair) ? current : pair));
      // Compare is almost always entered *from* one instance's step wheel, which leaves that
      // instance focused and one of its steps selected — and both of those dim the map down to
      // the very route the comparison is supposed to be showing against the other. Entering
      // clears them, so the first thing on screen is the pair, whole.
      setHighlightedInstanceId(null);
      select(null);
    },
    [
      onEnterCompare,
      appliedScope,
      comparePair,
      highlightedInstanceId,
      scopeIndex,
      select,
      selection,
    ],
  );

  /** The chooser hands the selected runs to the same graph used by the run-pair view. */
  const startComparison = useCallback(
    (next: ProcessComparison) => {
      if (!next.a.runs.length || !next.b.runs.length) return;
      const singleRuns = next.mode === "runs";
      const aId = next.a.runs[0].id;
      const bId = next.b.runs[0].id;
      if (singleRuns && scopeIndex.order.includes(aId) && scopeIndex.order.includes(bId)) {
        startCompare({ aId, bId });
        return;
      }
      if (!comparePair && onEnterCompare) {
        onEnterCompare(processComparisonHash(getBrowserHash(), next));
        return;
      }
      if (!comparePair)
        setCompareOrigin({
          scope: appliedScope,
          instanceId: highlightedInstanceId,
          stepIndex: selection?.kind === "node" ? (selection.occurrence?.stepIndex ?? null) : null,
        });
      setComparePair(null);
      setComparison(next);
      setHighlightedInstanceId(null);
      select(null);
    },
    [
      onEnterCompare,
      scopeIndex,
      startCompare,
      comparePair,
      appliedScope,
      highlightedInstanceId,
      selection,
      select,
    ],
  );

  const swapComparison = useCallback(() => {
    if (comparison) {
      setComparison({
        ...comparison,
        a: comparison.b,
        b: comparison.a,
        period: comparison.period ? { a: comparison.period.b, b: comparison.period.a } : undefined,
      });
      setHighlightedInstanceId(null);
      select(null);
    } else if (comparePair) startCompare({ aId: comparePair.bId, bId: comparePair.aId });
  }, [comparison, comparePair, startCompare, select]);

  /** Swap one side for another instance, keeping the other where it is. */
  const setCompareInstance = useCallback(
    (side: CompareSide, instanceId: string) => {
      if (!comparePair) return;
      startCompare(withCompareSide(comparePair, side, instanceId));
    },
    [comparePair, startCompare],
  );

  /**
   * Leave compare, back to where it was entered from: the scope that was applied then comes
   * back, and `compareOrigin` — read *before* calling this — tells the panel which instance and
   * step to reopen. A direct comparison link retains its base scope for the same reason.
   */
  const clearCompare = useCallback(() => {
    if (compareOrigin) applyScope(compareOrigin.scope);
    setCompareOrigin(null);
    setComparePair(null);
    setComparison(null);
    if (comparison) select(null);
  }, [applyScope, compareOrigin, comparison, select]);
  const exitCompare = useCallback(() => {
    if (onExitCompare?.()) return true;
    clearCompare();
    return false;
  }, [onExitCompare, clearCompare]);

  // Applying a different scope ends a pair comparison; swapping sides does not edit that scope.
  const compareScopeRef = useRef(appliedScope);
  useEffect(() => {
    const scopeChanged = compareScopeRef.current !== appliedScope;
    compareScopeRef.current = appliedScope;
    if (!comparePair || comparison) return;
    const known = new Set(scopeIndex.order);
    if (!scopeChanged && known.has(comparePair.aId) && known.has(comparePair.bId)) {
      return;
    }
    setComparePair(null);
    setCompareOrigin(null);
  }, [comparison, comparePair, appliedScope, scopeIndex]);

  useEffect(() => {
    writeProcessComparePair(runComparePair ? [runComparePair.aId, runComparePair.bId] : null);
  }, [runComparePair]);

  const previousInheritedScope = useRef(inheritedScopeKey);
  useEffect(() => {
    if (previousInheritedScope.current === inheritedScopeKey) return;
    previousInheritedScope.current = inheritedScopeKey;
    clearCompare();
    setPageScope(null);
  }, [inheritedScopeKey, clearCompare]);

  /**
   * Resolve a plain-language condition into a chip. It stays its own criterion rather than being
   * rewritten into user/date/node ones: the endpoint answers with instance ids and a sentence,
   * so any structure shown alongside them would be invented here rather than reported.
   */
  const resolveScopeQuery = useCallback(
    async (text: string): Promise<InstanceScopeQuery | null> => {
      const requestId = ++scopeQueryRequestId.current;
      setScopeQueryStatus("loading");
      try {
        const result = await filter_process_instances(
          process.slug,
          text,
          hasInheritedScope ? undefined : userIdList,
        );
        if (scopeQueryRequestId.current !== requestId) return null;
        if (result.instanceIds.length === 0) {
          setScopeQueryStatus("no_matches");
          return null;
        }
        setScopeQueryStatus("idle");
        return { text, instanceIds: result.instanceIds, explanation: result.explanation ?? null };
      } catch {
        if (scopeQueryRequestId.current === requestId) setScopeQueryStatus("error");
        return null;
      }
    },
    [process.slug, userIdList, hasInheritedScope],
  );

  /** Abandon any in-flight condition and drop the status line. */
  const resetScopeQuery = useCallback(() => {
    scopeQueryRequestId.current += 1;
    setScopeQueryStatus("idle");
  }, []);

  return {
    process: comparisonData?.process ?? process,
    baseProcess: process,
    filteredInstances,
    analysisGraph,
    simplified,
    canSimplify,
    commonPathsExample: !comparisonData && drawnMatch.commonPathsExample,
    allPathsRevealed,
    setAllPathsRevealed,

    processInstances: comparisonData?.process.instances ?? processInstances,
    selectedInstanceIds,
    selectedInstances,

    graph,
    layout,
    nodes,
    edges,
    reversedMap,
    layoutPending,
    layoutFailed,
    stepStats,
    nameById,

    colorBy,
    setColorBy,
    stepReading,
    setStepReading,
    edgeRouting,
    setEdgeRouting,
    traffic,
    setTraffic,
    simpleMapLimit,
    setSimpleMapStepLimit,

    selection,
    selectedNode,
    select,
    highlightedInstanceId,
    highlightedInstance,
    inspectedInstanceGraph,
    setHighlightedInstanceId,
    branchHoverEdge,
    setBranchHoverEdge,
    mapFocus,
    setMapFocus,

    instanceOptionIdentity,
    chatFilters,
    chatSessionKey,
    chatSuggestions,

    scopeIndex,
    appliedScope,
    applyScope,

    scopeQueryStatus,
    filtersOpen,
    setFiltersOpen,
    resolveScopeQuery,
    resetScopeQuery,

    comparison,
    comparisonData,
    startComparison,
    swapComparison,
    comparePair,
    compareOrigin,
    startCompare,
    setCompareInstance,
    exitCompare,
  };
};
